import React from 'react';

// Help center FAQs, grouped by topic. Order here is the order shown on the page:
// on desktop it fills the left column top to bottom, then the right column.
// Groups are arranged so each column holds whole topics (15 + 15).
const FAQS = [
  // About Scallop
  {
    q: 'What is Scallop App?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Scallop is a global financial platform that allows you to store, send, receive, and manage money across multiple fiat currencies and digital assets within a single app.
      </p>
      </>
    ),
  },
  {
    q: 'Who can use Scallop?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Both individuals and businesses can use Scallop. Individuals can manage personal finances, while businesses can handle payments, collections, and multi-currency operations.
      </p>
      </>
    ),
  },
  {
    q: 'Is Scallop available in my country?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Scallop supports onboarding across 120+ regions, with some exclusions due to international sanctions or regulatory limitations.
      
      A full list of prohibited countries & regions can be viewed here:
      https://docs.scallopx.com/details/onboarding-regions
      </p>
      </>
    ),
  },
  {
    q: 'Can I use Scallop for everyday spending?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, you can use virtual cards for online and global payments, or Scallop Pay to pay with local QR code payment providers.
      </p>
      </>
    ),
  },
  {
    q: 'Can I use Scallop while traveling?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, Scallop is designed for global usage, including payments and connectivity via eSIM.
      </p>
      </>
    ),
  },
  {
    q: 'Is my money safe on Scallop?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Scallop applies security controls, compliance checks, and monitoring to protect user funds and transactions.
      </p>
      </>
    ),
  },
  // Accounts & money
  {
    q: 'Can I hold multiple currencies at the same time?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, you can hold and manage up to 52 fiat currencies and digital assets in one account.
      </p>
      </>
    ),
  },
  {
    q: 'Can I track my transactions?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, all transactions can be viewed and monitored within the app in real time.
      </p>
      </>
    ),
  },
  {
    q: 'Can I send and receive money internationally?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, Scallop supports international payments depending on your tier and supported payment rails and currencies.
      </p>
      </>
    ),
  },
  {
    q: 'How long do money transfers take?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Transfer times vary by country and payment method. Some transfers are instant, while others may take longer depending on the payment rail.
      </p>
      </>
    ),
  },
  {
    q: 'Do I earn interest on my balance?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Interest or yield is not currently available but is planned as a future feature, depending on your tier.
      </p>
      </>
    ),
  },
  {
    q: 'What happens if I send digital assets to the wrong network?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Recovery is not guaranteed. You may need to contact support, and recovery may involve fees and delays.
      </p>
      </>
    ),
  },
  // Swaps
  {
    q: 'Can I convert between currencies in the app?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, Scallop allows you to swap between supported fiat currencies and digital assets instantly.
      </p>
      </>
    ),
  },
  {
    q: 'What are the swap fees?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Fees depend on your tier, with lower fees applied to higher tiers.
      </p>
      </>
    ),
  },
  {
    q: 'Can I reverse a swap transaction?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      No, all swaps are final once confirmed.
      </p>
      </>
    ),
  },
  // Verification & sign-in
  {
    q: 'Do I need to complete identity verification (KYC)?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, all users must complete KYC before accessing core features. You can preview the app before completing verification.
      
      The process takes around 3 minutes and requires one of the following documents:
      a passport, a national ID (non-US only), or a tax identification number such as an SSN or TIN.
      
      For more details, please refer to:
      https://docs.scallopx.com/details/requirements-for-individuals
      </p>
      </>
    ),
  },
  {
    q: 'Do I need to provide proof of address?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Proof of address is required only in specific cases, such as regulatory checks or additional verification requirements.
      </p>
      </>
    ),
  },
  {
    q: 'Is a passport mandatory?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      A passport may be required for certain features, such as virtual card issuance, if not already provided during onboarding.
      </p>
      </>
    ),
  },
  {
    q: 'What happens if I forget my password?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      You do not need a password. You can sign in using your email address.
      
      For more details, please refer to:
      How to Sign Up
      </p>
      </>
    ),
  },
  // Virtual cards
  {
    q: 'What types of cards does Scallop provide?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Scallop provides virtual Visa cards that can be used for online and global payments, as well as in-store purchases via Apple Pay and Google Pay.
      </p>
      </>
    ),
  },
  {
    q: 'How much do virtual cards cost?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Pricing depends on your tier:
      
      Voyager: $2 per card
      
      Mariner: First card free, additional cards at a small fee
      
      Admiral: Multiple cards included at no additional cost
      </p>
      </>
    ),
  },
  {
    q: 'How many virtual cards can I have active at the same time?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      You can have up to three active virtual cards at any time. To order a new card when you already have three active cards, you’ll first need to deactivate one of your existing cards.
      </p>
      </>
    ),
  },
  {
    q: 'Can I manage my virtual card?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, you can block, unblock, and manage your card settings directly from the app at any time.
      </p>
      </>
    ),
  },
  {
    q: 'Can I add my card to Apple Pay or Google Pay?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, the Scallop virtual card can be added as a payment method to Apple Pay and Google Pay.
      </p>
      </>
    ),
  },
  {
    q: 'Can I withdraw funds from a virtual card?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      No, funds loaded onto a virtual card are reserved for spending and cannot be withdrawn or reversed.
      </p>
      </>
    ),
  },
  // eSIM
  {
    q: 'What is Scallop+ eSIM?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Scallop+ eSIM is a built-in digital SIM service that provides mobile data access across multiple countries without a physical SIM card.
      
      For more details, please refer to:
      https://docs.scallopx.com/details/scallop+-esim
      </p>
      </>
    ),
  },
  {
    q: 'Can I top up my eSIM plan?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      Yes, you can add data or extend validity at any time, even after expiry.
      </p>
      </>
    ),
  },
  {
    q: 'What happens when my eSIM expires?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      The eSIM remains on your device, but data services stop until you renew or purchase a new plan.
      </p>
      </>
    ),
  },
  {
    q: 'Can I reuse a deleted eSIM?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      No, once deleted, the eSIM cannot be reused or restored.
      </p>
      </>
    ),
  },
  // Support
  {
    q: 'How can I contact support?',
    a: (
      <>
      <p dir="auto" className="framer-text">
      You can reach support directly through the app or by email at support@scallop.zohodesk.eu
      </p>
      </>
    ),
  },
];

export default FAQS;
